import { useState, useRef } from 'react';
import { Card, Title, Text } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { AgentCategories } from '@tiendanube/common';
import { Domain, AiFeature } from '@tiendanube/common/src/enums';
import FeatureFlag from 'App/components/FeatureFlag';
import ModalAside from 'App/components/lab/ModalAside';
import { AI_PRODUCT_CATEGORY_SUGGESTIONS_ENABLED } from 'App/featuresFlags';
import { AiFeedbackModal } from 'commons/components/AiFeedbackModal';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import Stack from 'commons/components/Stack';
import {
  useAiField,
  useAiGeneratedValues,
  useTranslationCommon,
} from 'commons/hooks';
import { AI_FIELDS } from 'commons/services/AgentChatService/AgentChatActionBusTypes';
import { useAiUsage } from 'domains/Ai/hooks';
import {
  trackingAiProductCategoryRemoveClick,
  trackingAiGenerateUseContentClick,
} from 'domains/Ai/tracking';
import { useGetCategories } from 'domains/Catalog/Categories/hooks';
import {
  SelectCategories,
  AiLoadingState,
  CategoriesDisplay,
} from './components';
import {
  useProductCategorySuggestionsWithAI,
  useCategoriesFeedback,
} from './hooks';
import useGenerateProduct from '../../hooks/useGenerateProduct/useGenerateProduct';
import { RequiredAiProperties } from '../Variants/types';

interface CategoriesProps {
  onChange: (categories: string[]) => void;
  selectedCategories: string[];
  requiredAiProperties: RequiredAiProperties;
}

// eslint-disable-next-line max-statements
function Categories({
  onChange,
  selectedCategories,
  requiredAiProperties,
}: Readonly<CategoriesProps>): JSX.Element {
  const { t } = useTranslation(Domain.CATALOG);
  const tCommon = useTranslationCommon();
  const categories = useGetCategories();
  const { generatedAICategories } = useGenerateProduct();
  const [showCategories, setShowCategories] = useState(false);
  const runIdRef = useRef<string | undefined>(undefined);
  const [showAiResults, setShowAiResults] = useState(false);
  const [aiGeneratedCategoryIds, setAiGeneratedCategoryIds] = useState(
    new Set(generatedAICategories),
  );

  const { isGeneratingAI, handleGenerateWithAI, getTooltipText } =
    useProductCategorySuggestionsWithAI({
      selectedCategories,
      requiredAiProperties,
      onCategoriesGenerated: (response) => {
        runIdRef.current = response.run_id;
        setShowAiResults(true);

        const aiCategoryIds = response.categories
          .map((aiCategory) => aiCategory.category_id.toString())
          .filter((categoryId) =>
            categories.some((category) => category.id === categoryId),
          );

        const newAiGeneratedIds = aiCategoryIds.filter(
          (categoryId) => !selectedCategories.includes(categoryId),
        );

        setAiGeneratedCategoryIds((prev) => {
          const updated = new Set<string>();

          for (const id of prev) {
            if (aiCategoryIds.includes(id)) {
              updated.add(id);
            }
          }

          for (const id of newAiGeneratedIds) {
            updated.add(id);
          }

          return updated;
        });

        const mergedCategoryIds = [
          ...new Set([...selectedCategories, ...aiCategoryIds]),
        ];

        onChange(mergedCategoryIds);

        trackingAiGenerateUseContentClick(
          { feature: AiFeature.AI_PRODUCT_CATEGORY_SUGGESTIONS },
          { categories_count: aiCategoryIds.length.toString() },
        );
      },
    });

  const { remainingAiUses } = useAiUsage();

  const {
    showFeedbackModal,
    handlePositiveFeedback,
    handleNegativeFeedback,
    closeFeedbackModal,
  } = useCategoriesFeedback({ runId: runIdRef.current });

  const handleHideCategores = () => {
    setShowCategories(false);
    if (showAiResults) {
      const hasAnyAiCategory = Array.from(aiGeneratedCategoryIds).some((aiId) =>
        selectedCategories.includes(aiId),
      );

      if (!hasAnyAiCategory) {
        setShowAiResults(false);
        runIdRef.current = undefined;
        setAiGeneratedCategoryIds(new Set());
      }
    }
  };

  const categoriesField = useAiField<AgentCategories[], string[]>({
    componentId: AI_FIELDS.PRODUCT_CATEGORIES,
    getValue: () => {
      const filteredCategories: AgentCategories[] = selectedCategories
        .map((categoryId): AgentCategories | null => {
          const category = categories.find((cat) => cat.id === categoryId);
          return category
            ? {
                category_id: Number.parseInt(category.id, 10),
                category_name: category.title,
                confidence_score: '0',
                proximity_score: '0',
              }
            : null;
        })
        .filter((cat): cat is AgentCategories => cat !== null);

      return filteredCategories;
    },

    onReplace: (value) => {
      const mappedCategories = value.map((category) =>
        category.category_id.toString(),
      );
      onChange(mappedCategories);
      return { aiGeneratedValues: mappedCategories };
    },
    onAdd: (value) => {
      const mappedCategories = value.map((category) =>
        category.category_id.toString(),
      );
      onChange([...selectedCategories, ...mappedCategories]);
      return { aiGeneratedValues: mappedCategories };
    },
    onRemove: (value) => {
      const mappedCategories = value.map((category) =>
        category.category_id.toString(),
      );
      onChange(
        selectedCategories.filter(
          (categoryId) => !mappedCategories.includes(categoryId),
        ),
      );
      return { aiGeneratedValues: mappedCategories };
    },
    onClear: () => {
      onChange([]);
    },
  });

  const categoriesFieldState = useAiGeneratedValues<string[]>(
    AI_FIELDS.PRODUCT_CATEGORIES,
  );

  const handleRemoveCategory = (categoryId: string) => {
    onChange(selectedCategories.filter((category) => category !== categoryId));

    if (aiGeneratedCategoryIds.has(categoryId)) {
      trackingAiProductCategoryRemoveClick({
        feature: AiFeature.AI_PRODUCT_CATEGORY_SUGGESTIONS,
      });

      const newAiGeneratedIds = new Set(aiGeneratedCategoryIds);
      newAiGeneratedIds.delete(categoryId);
      setAiGeneratedCategoryIds(newAiGeneratedIds);

      if (newAiGeneratedIds.size === 0) {
        setShowAiResults(false);
        runIdRef.current = undefined;
      }
    }

    categoriesFieldState.resetAiGeneratedValues({
      handler: (prevCategories) =>
        prevCategories.filter((category) => category !== categoryId),
    });
  };

  const handleOnOpen = () => {
    setShowCategories(true);
  };

  const hasProductData = requiredAiProperties?.name;
  const tooltipText = getTooltipText();
  const hasAiCategories = showAiResults && aiGeneratedCategoryIds.size > 0;

  const displayCategories = categories.map((cat) => ({
    ...cat,
    isAiGenerated:
      aiGeneratedCategoryIds.has(cat.id) ||
      categoriesFieldState.aiGeneratedValues?.includes(cat.id),
  }));

  return (
    <Card>
      <Card.Header>
        <Stack spacing="tight">
          <Title as="h4">{t('products.detail.categories')}</Title>
          {hasProductData && (
            <FeatureFlag flag={AI_PRODUCT_CATEGORY_SUGGESTIONS_ENABLED}>
              <GenerateWithAiLink
                trackingFeature={AiFeature.AI_PRODUCT_CATEGORY_SUGGESTIONS}
                fontSize="caption"
                onClick={handleGenerateWithAI}
                disabled={!!tooltipText || isGeneratingAI}
                tooltipText={tooltipText}
                tooltipMaxWidth="220px"
              />
            </FeatureFlag>
          )}
        </Stack>
      </Card.Header>
      <Card.Body>
        <Stack column align="stretch">
          {!selectedCategories.length && !isGeneratingAI && (
            <Stack.Item>
              <Text>{`${t('products.detail.infoCategories')}`}</Text>
            </Stack.Item>
          )}

          {isGeneratingAI && (
            <Stack.Item>
              <AiLoadingState />
            </Stack.Item>
          )}

          {!isGeneratingAI && (
            <Stack.Item {...categoriesField.navigationAttributes}>
              <CategoriesDisplay
                selectedCategories={selectedCategories}
                allCategories={displayCategories}
                onRemoveCategory={handleRemoveCategory}
                onEditCategories={handleOnOpen}
                feedbackMessage={
                  hasAiCategories
                    ? t(
                        'products.detail.seo.aiCategorySuggestions.categoriesSelectedByAI',
                      )
                    : undefined
                }
                thumbsUpAriaLabel={
                  hasAiCategories
                    ? tCommon('aiFeedbackModal.thumbsUpAriaLabel')
                    : undefined
                }
                thumbsDownAriaLabel={
                  hasAiCategories
                    ? tCommon('aiFeedbackModal.thumbsDownAriaLabel')
                    : undefined
                }
                onPositiveFeedback={
                  hasAiCategories ? handlePositiveFeedback : undefined
                }
                onNegativeFeedback={
                  hasAiCategories ? handleNegativeFeedback : undefined
                }
                remainingAiUses={remainingAiUses}
              />
            </Stack.Item>
          )}
        </Stack>
      </Card.Body>

      <ModalAside
        isOpen={showCategories}
        onDidDismiss={handleHideCategores}
        headerContent={t('products.detail.categories')}
      >
        <SelectCategories selecteds={selectedCategories} onChange={onChange} />
      </ModalAside>

      <AiFeedbackModal
        show={showFeedbackModal}
        title={t(
          'products.detail.seo.aiCategorySuggestions.feedbackModal.title',
        )}
        text={t('products.detail.seo.aiCategorySuggestions.feedbackModal.text')}
        feature={AiFeature.AI_PRODUCT_CATEGORY_SUGGESTIONS}
        feedbackParams={{ run_id: runIdRef.current }}
        onDismiss={closeFeedbackModal}
      />
    </Card>
  );
}

export default Categories;
